import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

export type Theme = "light" | "dark" | "system";
export type Contrast = "standard" | "increased" | "high";


interface ThemeContextType {
  theme: Theme;
  resolvedTheme: "light" | "dark";
  contrast: Contrast;
  setTheme: (theme: Theme) => void;
  setContrast: (contrast: Contrast) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// These two keys, and the defaults below, are read a second time by the inline
// script in public/index.html, which puts the theme on <html> before the first
// paint (BUG-64). Renaming a key or moving a default here without changing it
// there brings the light flash back for whoever had chosen dark.
const THEME_STORAGE_KEY = "unikort-theme";
const CONTRAST_STORAGE_KEY = "unikort-contrast";
const PRESET_STORAGE_KEY = "unikort-theme-preset";

function getSystemTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function getStoredTheme(): Theme {
  if (typeof window === "undefined") return "system";
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }
  return "system";
}

/**
 * `increased` is the default — it is what the settings screen calls «Стандартная».
 * The three values keep their historical names so that a stored choice and the
 * `[data-contrast]` selectors in tokens.css stay valid; only the labels and the
 * default moved. `standard` is now the softest of the three.
 */
const DEFAULT_CONTRAST: Contrast = "increased";

function getStoredContrast(): Contrast {
  if (typeof window === "undefined") return DEFAULT_CONTRAST;
  const stored = localStorage.getItem(CONTRAST_STORAGE_KEY);
  if (stored === "standard" || stored === "increased" || stored === "high") {
    return stored;
  }
  return DEFAULT_CONTRAST;
}

/**
 * Keeps the browser chrome (iOS Safari's tab bar, Android's address bar) the
 * colour of the header surface. Once a theme is chosen by hand the two
 * media-scoped <meta theme-color> tags from index.html are collapsed into one
 * unscoped tag, otherwise Safari keeps following the system scheme.
 */
const THEME_COLOR: Record<"light" | "dark", string> = { light: "#ffffff", dark: "#1c1c1e" };
function applyThemeColor(resolved: "light" | "dark") {
  if (typeof document === "undefined") return;
  const metas = Array.from(document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]'));
  if (metas.length === 0) return;
  metas.slice(1).forEach((m) => m.remove());
  const meta = metas[0];
  meta.removeAttribute("media");
  meta.setAttribute("content", THEME_COLOR[resolved]);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(getStoredTheme);
  const [contrast, setContrastState] = useState<Contrast>(getStoredContrast);
  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">(() => {
    const stored = getStoredTheme();
    return stored === "system" ? getSystemTheme() : stored;
  });

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    const resolved = theme === "system" ? getSystemTheme() : theme;

    setResolvedTheme(resolved);

    applyThemeColor(resolved);
    if (resolved === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
  }, [theme]);

  // Apply contrast to document; "standard" keeps the default tokens and
  // sets no attribute (tokens.css scopes overrides to [data-contrast])
  useEffect(() => {
    const root = document.documentElement;
    if (contrast === "standard") {
      delete root.dataset.contrast;
    } else {
      root.dataset.contrast = contrast;
    }
  }, [contrast]);

  // The colour-theme picker was retired with the Settings profile and security
  // blocks (BUG-22). Nothing can choose a palette any more, so a value the old
  // picker stored must stop applying — otherwise whoever had picked Ocean is
  // left on it for good with no way back (Codex review). Clearing the key is
  // what makes the removal actually take effect for them.
  useEffect(() => {
    localStorage.removeItem(PRESET_STORAGE_KEY);
    delete document.documentElement.dataset.themePreset;
  }, []);

  // Listen for system theme changes
  useEffect(() => {
    if (theme !== "system") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      const newResolvedTheme = e.matches ? "dark" : "light";
      setResolvedTheme(newResolvedTheme);

      const root = document.documentElement;
      applyThemeColor(newResolvedTheme);
      if (newResolvedTheme === "dark") {
        root.classList.add("dark");
      } else {
        root.classList.remove("dark");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
  };

  const setContrast = (newContrast: Contrast) => {
    setContrastState(newContrast);
    localStorage.setItem(CONTRAST_STORAGE_KEY, newContrast);
  };

  return (
    <ThemeContext.Provider
      value={{ theme, resolvedTheme, contrast, setTheme, setContrast }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
